import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Grid, TextField } from "@mui/material";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import LGAutocomplete from "../../atoms/Common/LGAutocomplete.tsx";
import MyDatePicker from "../../atoms/Common/MyDatePicker";
import { getProductList } from "../../api/products";
import { getStockMoveById } from "../../api/stockmoves/index.ts";
import syntheticStockMoveService from "../../services/syntheticStockMoveService";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";

const schema = Yup.object().shape({
  product: Yup.object().nullable().required("El producto es obligatorio"),
  quantity: Yup.number()
    .required("La cantidad es obligatoria")
    .min(0.0001, "La cantidad debe ser mayor que cero"),
  unitPrice: Yup.number()
    .nullable()
    .transform((value, original) => (original === "" ? null : value))
    .min(0, "El costo no puede ser negativo"),
  moveDate: Yup.mixed().nullable().required("La fecha de movimiento es obligatoria"),
});

const makeEmptyValues = () => ({
  product: null,
  quantity: "",
  unitPrice: "",
  moveDate: dayjs(),
});

export default function SyntheticOutboundStockMoveForm({ id, companyId, onSubmit }) {
  const { openSnack } = useSnack();
  const [loading, setLoading] = useState(Boolean(id));
  const [initialValues, setInitialValues] = useState(makeEmptyValues());

  useEffect(() => {
    if (!id) {
      setInitialValues(makeEmptyValues());
      setLoading(false);
      return;
    }

    const fetchById = async () => {
      setLoading(true);
      try {
        const row = await getStockMoveById(id);
        setInitialValues({
          product: row.product
            ? { ...row.product }
            : row.productId
              ? { id: row.productId, code: "" }
              : null,
          quantity: Number(row.quantity) || 0,
          unitPrice: row.unitCost != null ? Number(row.unitCost) : 0,
          moveDate: row.moveDate ? dayjs(row.moveDate) : dayjs(),
        });
      } catch (error) {
        openSnack(handleError(error), "error");
      } finally {
        setLoading(false);
      }
    };

    fetchById();
  }, [id, openSnack]);

  const handleSave = async (values) => {
    try {
      const payload = {
        ProductId: values.product?.id,
        Quantity: Number(values.quantity) || 0,
        UnitCost:
          values.unitPrice !== "" && values.unitPrice != null
            ? Number(values.unitPrice)
            : null,
        MoveDate: values.moveDate ? dayjs(values.moveDate).toDate() : new Date(),
      };

      if (!id) {
        await syntheticStockMoveService.createManualSyntheticOutbound({
          ...payload,
          CompanyId: companyId,
        });
        openSnack("Salida sintética creada correctamente", "success");
      } else {
        await syntheticStockMoveService.updateSyntheticOutbound(id, payload);
        openSnack("Salida sintética actualizada correctamente", "success");
      }
      onSubmit?.();
    } catch (error) {
      openSnack(handleError(error), "error");
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={handleSave}
      enableReinitialize
    >
      {({ values, errors, touched, setFieldValue }) => (
        <Form>
          <Grid container spacing={2} sx={{ p: 2 }}>
            <Grid item xs={12}>
              <LGAutocomplete
                fetchFunction={getProductList}
                queryParams={{ companyId, pageSize: 20 }}
                optionLabel="code"
                value={values.product}
                onChange={(_, value) => setFieldValue("product", value)}
                label="Producto"
                error={Boolean(touched.product && errors.product)}
                helperText={touched.product ? errors.product : ""}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <MyDatePicker
                label="Fecha movimiento"
                value={values.moveDate}
                onChange={(value) => setFieldValue("moveDate", value)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cantidad"
                type="number"
                value={values.quantity}
                onChange={(event) =>
                  setFieldValue(
                    "quantity",
                    event.target.value === "" ? "" : Number(event.target.value)
                  )
                }
                error={Boolean(touched.quantity && errors.quantity)}
                helperText={touched.quantity ? errors.quantity : ""}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Costo unitario"
                type="number"
                value={values.unitPrice}
                onChange={(event) =>
                  setFieldValue(
                    "unitPrice",
                    event.target.value === "" ? "" : Number(event.target.value)
                  )
                }
                error={Boolean(touched.unitPrice && errors.unitPrice)}
                helperText={touched.unitPrice ? errors.unitPrice : ""}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button type="submit" variant="contained">
                  Guardar
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
}
